import React, { useEffect } from 'react';
import { I18n } from '@aws-amplify/core';
import type { ColumnsType } from 'antd/lib/table';
import type { TableRowSelection } from 'antd/lib/table/interface';
import type { Rule } from 'antd/lib/form';

import { SubTitle } from './SubTitle';
import { Filters } from './Filters';
import { formatDate, showTotal } from './utils';
import { useFileSearch } from './useFileSearch';
import { useAutoSelectFiles } from './useAutoSelectFiles';
import { useBeraJobStepsContext } from '../context';
import { FileForCustomReport } from '@/components/ui/Inputs/types/response/file';
import { Content, FilesTable, StepContainer, TableFormItem } from './styles';

interface FilesSelectionProps {
	isLoading?: boolean;
}

function buildFilesListRules(selectedFilesId: string[] | React.Key[]): Rule[] {
	const rule = {
		validator(): Promise<void> {
			const hasSelectedFile = !!selectedFilesId?.length && !!selectedFilesId[0];

			if (!hasSelectedFile) {
				return Promise.reject(I18n.get('Select at least one file'));
			}

			return Promise.resolve();
		}
	};

	return [rule];
}

export function FilesSelection({ isLoading = false }: Readonly<FilesSelectionProps>) {
	const { isSearchingFile, handleSearchFileNameChange } = useFileSearch();

	const {
		page,
		limit,
		beraFiles,
		beraJobSummary,
		selectedFilesId,
		filterSelectedFiles,
		handleSelectedFilesChange,
		handlePageChange,
		handleIsFileSelectionPageChange
	} = useBeraJobStepsContext();

	const { selectedRowKeys, handleSelectChange, handleRowSelect, isFileDisabled, isSingleSelection } =
		useAutoSelectFiles({
			beraFiles,
			beraJobSummary,
			selectedFilesId,
			filterSelectedFiles,
			handleSelectedFilesChange
		});

	useEffect(() => {
		handleIsFileSelectionPageChange(true);
		return () => {
			handleIsFileSelectionPageChange(false);
		};
	}, [handleIsFileSelectionPageChange]);

	const columns: ColumnsType<FileForCustomReport> = [
		{
			title: I18n.get('File'),
			dataIndex: 'original_name',
			key: 'original_name',
			align: 'center',
			ellipsis: true
		},
		{
			title: I18n.get('Load date'),
			dataIndex: 'created_at',
			key: 'created_at',
			align: 'center',
			render: formatDate
		}
	];

	function sortFiles(files: FileForCustomReport[]) {
		return files.slice().sort((a, b) => {
			const getPriority = (file: FileForCustomReport) => {
				if (file.bera_report?.length) return 0;
				if (file.bera_job_summary_files) return 1;
				return 2;
			};
			return getPriority(a) - getPriority(b);
		});
	}

	let dataSource = sortFiles(beraFiles?.files?.rows || []);

	let selectionType: TableRowSelection<FileForCustomReport>['type'] = 'checkbox';

	if (isSingleSelection) {
		selectionType = 'radio';
	}

	const rowSelection: TableRowSelection<FileForCustomReport> = {
		type: selectionType,
		selectedRowKeys,
		onChange: handleSelectChange,
		getCheckboxProps: (record: FileForCustomReport) => ({
			disabled: isFileDisabled(record)
		})
	};

	function handleRowClick(event: React.MouseEvent<HTMLElement>, record: FileForCustomReport): void {
		const target = event.target instanceof HTMLElement ? event.target : undefined;
		const isSelectionControl = !!target?.closest('label');

		if (isSelectionControl) {
			return;
		}

		handleRowSelect(record);
	}

	function getRowProps(record: FileForCustomReport): React.HTMLAttributes<HTMLElement> {
		const isDisabled = isFileDisabled(record);
		const result = {
			style: { cursor: isDisabled ? 'not-allowed' : 'pointer' },
			onClick: (event: React.MouseEvent<HTMLElement>) => handleRowClick(event, record)
		};

		return result;
	}

	function showRowSelection(): TableRowSelection<FileForCustomReport> | undefined {
		return rowSelection;
	}

	const filesListRules = buildFilesListRules(selectedFilesId);

	const paginationOptions = {
		hideOnSinglePage: false,
		showSizeChanger: false,
		showTotal,
		total: beraFiles?.files?.count,
		current: page === 0 ? page + 1 : page,
		defaultPageSize: limit,
		pageSize: limit,
		onChange: (page: number) => {
			handlePageChange(page);
		}
	};

	return (
		<StepContainer>
			<SubTitle isSingleSelection={isSingleSelection} />
			<Content>
				<Filters onSearchFileNameChange={handleSearchFileNameChange} />
				<TableFormItem name={['files_list']} rules={filesListRules}>
					<FilesTable
						rowKey="id"
						columns={columns}
						dataSource={dataSource}
						pagination={paginationOptions}
						rowSelection={showRowSelection()}
						loading={isLoading || isSearchingFile}
						onRow={getRowProps}
					/>
				</TableFormItem>
			</Content>
		</StepContainer>
	);
}
